import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import Field from "@/components/FormInput/Field";
import { ErrorComponent } from "./ErrorComponent";
import AuthShell from "../AuthShell";
import { LoginFormData } from "./types";

const SignInForm = (props: LoginFormData & { redirectUrl: string | null }) => {
  const {
    onSubmit,
    reset,
    formHandleSubmit,
    register,
    errors,
    language,
    getValues,
    redirectUrl,
    bannerError,
  } = props;

  const navigate = useNavigate();

  // TODO remove any type
  const { isRequesting, emailAddressFromParams, isVerifyingEmail } =
    useSelector((state: any) => state.metadata);

  useEffect(() => {
    if (emailAddressFromParams) {
      reset({ email: emailAddressFromParams });
    }
  }, [emailAddressFromParams, reset]);

  const { t } = useTranslation(["translation.login"]);

  const baseUrl = language === "ESP" ? "/es" : "";

  const signup = () => {
    if (sessionStorage.getItem("scrollDetails")) {
      const sessionData = JSON.parse(
        sessionStorage.getItem("scrollDetails") || "{}"
      );
      sessionStorage.setItem(
        "scrollDetails",
        JSON.stringify({ ...sessionData, path: "/auth/sign-up" })
      );
    }
    let url = "/auth/sign-up";
    if (redirectUrl) {
      url += `?redirect_url=${redirectUrl}`;
    }
    navigate(url);
  };

  const forgotPasswordPage = (event: React.SyntheticEvent) => {
    event?.preventDefault();
    navigate("/forgot-password");
  };

  const emailHelperText: string | undefined = errors?.email
    ? String(errors.email.message)
    : emailAddressFromParams !== "" &&
        !isVerifyingEmail &&
        getValues("email") !== emailAddressFromParams
      ? t("UnableToAcceptInvitation")
      : undefined;

  return (
    <AuthShell
      eyebrow={t("Login")}
      title={`${t("WelcomeBack")}, Rebel!`}
      onSubmit={formHandleSubmit(onSubmit)}
      submitLabel={t("Login")}
      submitting={isRequesting}
      bannerError={bannerError && <ErrorComponent>{bannerError}</ErrorComponent>}
      logoHref={`https://rebelbase.co${baseUrl}`}
      footerPrompt={t("NoAccount")}
      footerActionLabel={t("Signup")}
      onFooterAction={signup}
    >
      <Field
        id="email"
        label={t("Email")}
        fullWidth
        {...register("email", { required: "Required" })}
        error={Boolean(errors?.email)}
        helperText={emailHelperText}
      />

      {/*
       * The mockup puts "Forgot your password?" inline with the Password
       * label, which `Field` can't do on its own — its `label` slot is a
       * single `InputLabel`, and nesting an interactive link inside a
       * `<label>` element is invalid markup. So the label is rendered here
       * instead, styled identically to Field's own internal one, and `Field`
       * below gets no `label` prop (it renders nothing for that slot).
       */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 4,
        }}
      >
        <InputLabel
          htmlFor="password"
          error={Boolean(errors?.password)}
          sx={{
            position: "static",
            transform: "none",
            typography: "subtitle1",
            color: "text.primary",
            "&.Mui-error": { color: "error.main" },
          }}
        >
          {t("Password")}
        </InputLabel>
        <Link
          underline="none"
          color="primary"
          onClick={forgotPasswordPage}
          sx={{ "&:hover": { color: (theme) => theme.palette.primary.main } }}
        >
          <Typography variant="body2" color="primary">
            {t("ForgotPassword")}
          </Typography>
        </Link>
      </Box>
      <Field
        id="password"
        type="password"
        fullWidth
        {...register("password", { required: "Required" })}
        error={Boolean(errors?.password)}
        helperText={errors?.password ? String(errors.password.message) : undefined}
      />
    </AuthShell>
  );
};

export default React.memo(SignInForm);
